/**
 * Impersonation Store
 *
 * Manages impersonation session state in sessionStorage.
 * Uses sessionStorage (same as client auth) to ensure impersonation
 * sessions are isolated per browser tab and don't persist across tab closes.
 *
 * Impersonation sessions have a 30-minute timeout enforced by the backend.
 * The `expiresAt` timestamp tracks when the session will expire.
 */

import { UserRole } from '@ata-digital/client-tax-system-api-client';
import { createLogger } from '@/utils/logger';

const log = createLogger('impersonationStore');

const STORAGE_KEY = 'impersonation_state';

/**
 * Data structure for impersonation session state.
 * Matches the ImpersonateClientResponseDto from the API client.
 */
export interface ImpersonationData {
  /** Indicates this is an impersonated session */
  isImpersonated: boolean;
  /** The original preparer user's ID */
  originalUserId: string;
  /** The original preparer user's email */
  originalUserEmail: string;
  /** Display name for the preparer (derived from email) */
  preparerDisplayName: string;
  /** The client ID being impersonated */
  impersonatedClientId: string;
  /** The user ID being impersonated within the client */
  impersonatedUserId: string;
  /** Unix timestamp (milliseconds) when the session expires */
  expiresAt: number;
  /** Role of the original user (impersonator) */
  originalUserRole: UserRole;
}

/**
 * Derives a display name from an email address.
 * e.g., "john.doe@example.com" -> "John Doe"
 *       "jsmith@example.com" -> "Jsmith"
 */
export function deriveDisplayNameFromEmail(email: string): string {
  if (!email) return 'Unknown';

  const localPart = email.split('@')[0];
  if (!localPart) return email;

  // Replace common separators with spaces and capitalize each word
  return localPart
    .replace(/[._-]/g, ' ')
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Impersonation store for managing preparer-as-client sessions.
 *
 * This store uses sessionStorage to persist impersonation state,
 * ensuring tab isolation and automatic cleanup on tab close.
 */
export const impersonationStore = {
  /**
   * Sets the impersonation session data in storage.
   * @param data - The impersonation session data to store
   */
  setImpersonation(data: ImpersonationData): void {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      log.debug('Impersonation state stored', {
        originalUserId: data.originalUserId,
        impersonatedClientId: data.impersonatedClientId,
        expiresAt: new Date(data.expiresAt).toISOString(),
      });
    } catch (error) {
      log.error('Failed to store impersonation state', error);
    }
  },

  /**
   * Retrieves the current impersonation session data.
   * Returns null if no impersonation is active or if the session has expired.
   * @returns The impersonation data or null
   */
  getImpersonation(): ImpersonationData | null {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (!stored) {
        return null;
      }

      const data = JSON.parse(stored) as ImpersonationData;

      // Check if session has expired
      if (data.expiresAt && Date.now() > data.expiresAt) {
        log.info('Impersonation session expired, clearing state');
        this.clearImpersonation();
        return null;
      }

      // Check for missing impersonatedUserId (older session format) - clear invalid session
      if (!data.impersonatedUserId) {
        log.info('Impersonation session missing impersonatedUserId (legacy format), clearing state');
        this.clearImpersonation();
        return null;
      }

      // Normalize for backwards compatibility - default to Preparer (safe: blocks signing)
      return {
        ...data,
        originalUserRole: data.originalUserRole ?? UserRole.Preparer,
      };
    } catch (error) {
      log.error('Failed to retrieve impersonation state', error);
      return null;
    }
  },

  /**
   * Clears the impersonation session from storage.
   * Should be called when ending an impersonation session.
   */
  clearImpersonation(): void {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
      log.debug('Impersonation state cleared');
    } catch (error) {
      log.error('Failed to clear impersonation state', error);
    }
  },

  /**
   * Checks if the current session is an impersonated session.
   * Also validates that the session hasn't expired.
   * @returns True if currently impersonating a client
   */
  isImpersonated(): boolean {
    const data = this.getImpersonation();
    return data?.isImpersonated === true;
  },

  /**
   * Gets the time remaining in the impersonation session.
   * @returns Seconds remaining until expiration, or null if not impersonating
   */
  getTimeRemaining(): number | null {
    const data = this.getImpersonation();
    if (!data || !data.expiresAt) {
      return null;
    }

    const remainingMs = data.expiresAt - Date.now();
    if (remainingMs <= 0) {
      this.clearImpersonation();
      return null;
    }

    return Math.floor(remainingMs / 1000);
  },
} as const;
